import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Popover,
  Typography,
} from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import type { Incident } from '../../services/incidentsApi';

type EvidencePopoverState = {
  open: boolean;
  anchorEl: HTMLElement | null;
  incident: Incident | null;
};

type IncidentEvidencePopoverProps = {
  evidencePopover: EvidencePopoverState;
  closeEvidencePopover: () => void;
  evidencePreviewUrlByIncident: Record<string, string>;
  evidencePreviewLoadingByIncident: Record<string, boolean>;
  evidencePreviewErrorByIncident: Record<string, string>;
  zoneNameById: Map<string, string>;
  fetchLatestSnapshotBlob: (incidentId: string) => Promise<Blob>;
  fetchLatestVideoBlob: (incidentId: string) => Promise<Blob>;
  downloadBlob: (blob: Blob, filename: string) => void;
  onOpenMetadata: (incident: Incident) => void;
};

const numOrNull = (value: any): number | null => {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = String(value).trim();
  if (!text) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function IncidentEvidencePopover({
  evidencePopover,
  closeEvidencePopover,
  evidencePreviewUrlByIncident,
  evidencePreviewLoadingByIncident,
  evidencePreviewErrorByIncident,
  zoneNameById,
  fetchLatestSnapshotBlob,
  fetchLatestVideoBlob,
  downloadBlob,
  onOpenMetadata,
}: IncidentEvidencePopoverProps) {
  return (
    <Popover
      open={evidencePopover.open}
      anchorEl={evidencePopover.anchorEl}
      onClose={closeEvidencePopover}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        onMouseLeave: closeEvidencePopover,
        sx: { p: 1.5, width: 420, maxWidth: '90vw' },
      }}
    >
      {(() => {
        const incident = evidencePopover.incident;
        if (!incident) return null;
        const incidentId = incident.incident_id;
        const snapshotFilename = incident.evidence?.latest?.snapshot?.filename;
        const videoFilename = (incident.evidence as any)?.latest?.video?.filename;
        const previewUrl = incidentId ? evidencePreviewUrlByIncident[incidentId] : '';
        const loading = incidentId ? evidencePreviewLoadingByIncident[incidentId] : false;
        const error = incidentId ? evidencePreviewErrorByIncident[incidentId] : '';
        const zoneLabel = zoneNameById.get(incident.zone_id || '') || incident.zone_id || '—';
        const policyLabel =
          (incident as any)?.triggered_by?.policy_name ||
          (incident as any)?.triggered_by?.policy_type ||
          (incident as any)?.policy_type ||
          (incident as any)?.policy_id ||
          '—';
        const confidenceRaw =
          (incident.metrics as any)?.attributes?.confidence ??
          (incident.metrics as any)?.confidence ??
          (incident.metrics as any)?.score ??
          null;
        const confidence =
          typeof confidenceRaw === 'number'
            ? `${Math.round(confidenceRaw * 100)}%`
            : (confidenceRaw != null ? String(confidenceRaw) : '—');

        const attachments = Array.isArray(incident.evidence?.attachments) ? (incident.evidence?.attachments || []) : [];
        const latestAttachment = attachments.slice().sort((a: any, b: any) => {
          const aTs = a?.uploaded_at ? Date.parse(a.uploaded_at) : 0;
          const bTs = b?.uploaded_at ? Date.parse(b.uploaded_at) : 0;
          return bTs - aTs;
        })[0];
        const capturedAtRaw =
          latestAttachment?.metadata?.captured_at ||
          latestAttachment?.metadata?.timestamp ||
          latestAttachment?.metadata?.extra?.capture_completed_at ||
          null;
        const capturedAt = capturedAtRaw ? new Date(String(capturedAtRaw)).toLocaleString() : null;
        const latencyMs = numOrNull(
          latestAttachment?.metadata?.capture_latency_ms ??
          latestAttachment?.metadata?.extra?.capture_latency_ms ??
          null,
        );
        const encodingMs = numOrNull(
          (latestAttachment?.metadata as any)?.encoding_latency_ms ??
          (latestAttachment?.metadata as any)?.extra?.encoding_latency_ms ??
          null,
        );
        const totalMs = numOrNull(
          (latestAttachment?.metadata as any)?.capture_total_latency_ms ??
          (latestAttachment?.metadata as any)?.extra?.capture_total_latency_ms ??
          null,
        );

        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Evidence
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {incidentId}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
              <Box>
                <Typography variant="caption" color="textSecondary">Zone</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{zoneLabel}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Policy</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{String(policyLabel)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Confidence</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{confidence}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Time</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {incident.created_at ? new Date(incident.created_at).toLocaleString() : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Snapshot captured</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {capturedAt || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Capture latency (excl. encoding)</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {latencyMs != null && Number.isFinite(latencyMs) ? `${Math.round(latencyMs)} ms` : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Encoding latency</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {encodingMs != null && Number.isFinite(encodingMs) ? `${Math.round(encodingMs)} ms` : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Total (capture + encoding)</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {totalMs != null && Number.isFinite(totalMs) ? `${Math.round(totalMs)} ms` : '—'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 1 }}>
              {error ? (
                <Alert severity="warning">{error}</Alert>
              ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : previewUrl ? (
                <Box
                  component="img"
                  alt="Snapshot preview"
                  src={previewUrl}
                  sx={{
                    width: '100%',
                    maxHeight: 260,
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.default',
                  }}
                />
              ) : (
                <Alert severity="info">Hover to load snapshot preview.</Alert>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<ImageIcon />}
                disabled={!snapshotFilename}
                onClick={async (event) => {
                  event.stopPropagation();
                  try {
                    const blob = await fetchLatestSnapshotBlob(incidentId);
                    downloadBlob(blob, snapshotFilename || `${incidentId}.jpg`);
                  } catch {
                    // ignore
                  }
                }}
              >
                Snapshot
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!videoFilename}
                onClick={async (event) => {
                  event.stopPropagation();
                  try {
                    const blob = await fetchLatestVideoBlob(incidentId);
                    downloadBlob(blob, videoFilename || `${incidentId}.mp4`);
                  } catch {
                    // ignore
                  }
                }}
              >
                5–10s Clip
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenMetadata(incident);
                }}
              >
                Metadata
              </Button>
            </Box>
          </Box>
        );
      })()}
    </Popover>
  );
}
